// The table in words, for a screen reader (UX review 2026-11-02): it said one thing in 8 minutes of play. What each update changed, from what any seat could see, and the
// seat's own draw. At a table without assists nothing says what the hand can do.

import { ActionType } from "../pb/mj/record/v1/record_pb";
import { t } from "../strings";
import { isTurn } from "./clock";
import { SEATS, unseen } from "./view";
import type { View } from "./view";
import { NO_TILE, isTile, tileWords } from "./tiles";

const who = (v: View, seat: number) => (seat === v.seat ? t.you : v.players[seat]?.name || t.deletedPlayer);
const words = (v: View, id: number) => tileWords(id, v.redFives);
const sentence = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** What changed from `prev` to `v`, to be said. */
export function spoken(prev: View, v: View, assisted: boolean): string[] {
  const out: string[] = [];
  if (v.hand && prev.hand?.index !== v.hand.index) out.push(t.say.hand(t.rounds[v.hand.round] ?? "", v.hand.handInRound + 1, who(v, v.hand.dealer)));
  const same = prev.hand?.index === v.hand?.index;
  for (let s = 0; s < SEATS; s++) {
    const a = v.seats[s];
    const b = same ? prev.seats[s] : undefined;
    if (!a) continue;
    for (const m of a.melds.slice(b?.melds.length ?? 0)) out.push(t.say.called(who(v, s), t.actions[ActionType[m.type] ?? ""] ?? "", m.tiles.map((x) => words(v, x)).join(", ")));
    for (const r of a.river.slice(b?.river.length ?? 0)) out.push(r.riichi ? t.say.riichi(who(v, s), words(v, r.tile)) : t.say.discards(who(v, s), words(v, r.tile)));
    if (s === v.seat && a.drawn !== NO_TILE && isTile(a.drawn) && a.drawn !== b?.drawn) out.push(t.say.drew(words(v, a.drawn)));
  }
  const p = v.prompt;
  if (p && !p.sent && p.prompt.promptId !== prev.prompt?.prompt.promptId) {
    if (isTurn(p.prompt.kind)) out.push(t.say.yourTurn);
    if (assisted) {
      const names = p.prompt.legal.filter((a) => a.type !== ActionType.DISCARD && a.type !== ActionType.PASS).map((a) => t.actions[ActionType[a.type] ?? ""] ?? "");
      if (names.length > 0) out.push(t.say.canCall([...new Set(names)].join(", ")));
    }
  }
  if (v.callout && v.callout.seq !== prev.callout?.seq && (v.callout.action === "RON" || v.callout.action === "TSUMO")) out.push(t.say.wins(who(v, v.callout.seat), t.actions[v.callout.action] ?? ""));
  if (v.result?.end && !prev.result?.end) out.push(t.say.handOver(v.seats.map((s, i) => `${who(v, i)} ${s.points}`).join(", ")));
  if (v.connections[v.seat]?.auto && !prev.connections[prev.seat]?.auto) out.push(t.forYouAuto);
  return out.map(sentence);
}

/** How the table stands, asked for by a key. */
export function spokenState(v: View): string {
  const me = v.seats[v.seat];
  if (!v.hand || !me) return t.say.noHand;
  const hand = [...me.concealed, ...(isTile(me.drawn) ? [me.drawn] : [])].map((x) => words(v, x)).join(", ");
  const seats = v.seats.map((s, i) => `${who(v, i)} ${s.points}${s.riichi ? `, ${t.actions.DECLARE_RIICHI}` : ""}`).join("; ");
  const waits = v.aids?.waits.length ? t.say.waits(v.aids.waits.map((k) => `${tileWords(k * 4 + 1, false)}, ${unseen(v, k)}`).join("; ")) : "";
  const turn = v.clock ? t.say.turnOf(who(v, v.clock.seat)) : "";
  return [t.say.hand(t.rounds[v.hand.round] ?? "", v.hand.handInRound + 1, who(v, v.hand.dealer)), `${v.hand.wallLeft} ${t.wallLeft}`, turn, seats, t.say.handIs(hand), waits].filter(Boolean).map(sentence).join(". ");
}
